<template>
    <div
        :style="[
            bgColor ? `background-color: ${bgColor}` : '',
            `padding-top: ${topPadding}px;padding-bottom: ${bottomPadding}px;`,
        ]"
    >
        <Container class="px-[18px]">
            <div v-if="heading" class="mb-[22px] md:mb-[26px]">
                <h2
                    class="text-[20px] md:text-[26px] font-[700] leading-tight"
                    :style="headingColor ? `color: ${headingColor}` : ''"
                >
                    {{ heading }}
                </h2>

                <hr
                    v-if="showRule"
                    class="mt-[10px] border-t"
                    :style="ruleColor ? `border-color: ${ruleColor}` : ''"
                />
            </div>

            <div class="grid gap-[18px] md:gap-[22px]" :class="columnsClass">
                <div
                    v-for="(item, index) in items"
                    :key="item.id ?? index"
                    class="rounded-[6px] overflow-hidden"
                    :class="spanClass(item)"
                    :style="cardStyle(item)"
                >
                    <!-- The accent bar is the inner element's start border, so it runs
                         the full height of the card however tall the copy makes it. -->
                    <div
                        class="h-full border-s-[4px] ps-[20px] pe-[22px] py-[18px] md:py-[20px]"
                        :style="accentStyle(item)"
                    >
                        <h3
                            class="text-[15px] md:text-[16px] font-[700] leading-snug"
                            :style="titleColor ? `color: ${titleColor}` : ''"
                        >
                            {{ item.title || 'Card title' }}
                        </h3>

                        <div
                            class="accent-card-body mt-[10px] text-[14px] md:text-[15px] leading-[1.65]"
                            :style="bodyColor ? `color: ${bodyColor}` : ''"
                        >
                            <div v-if="item.body" v-html="item.body"></div>
                            <p v-else class="italic opacity-60">
                                No text has been added to this card yet.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </div>
</template>

<script>
import Container from '../../../Layouts/Container.vue'

// Tailwind scans this file for literal class names, so the column and span
// classes have to be written out rather than built by interpolation.
const COLUMN_CLASSES = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
}

// A full-width card may only span as far as the grid is wide -- spanning 3 in a
// 2-column grid would make the browser open an implicit third column and throw
// the rest of the row out of alignment.
const SPAN_CLASSES = {
    1: '',
    2: 'md:col-span-2',
    3: 'md:col-span-2 lg:col-span-3',
}

export default {
    components: {
        Container,
    },
    props: {
        isInEditor: {
            type: Boolean,
            default: false,
        },
        heading: {
            type: String,
            default: '',
        },
        // The rule under the heading: the design uses a hairline tinted to match
        // the cards below it, which basics/hairline cannot produce.
        showRule: {
            type: Boolean,
            default: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        cardsPerRow: {
            type: Number,
            default: 2,
        },
        // Section-wide card colours. A card may override either one, which is how
        // a section mixes accents (green and blue) without repeating every field.
        accentColor: {
            type: String,
            default: '#A4EBA2',
        },
        cardBgColor: {
            type: String,
            default: '#F6FDF6',
        },
        titleColor: {
            type: String,
            default: null,
        },
        bodyColor: {
            type: String,
            default: null,
        },
        headingColor: {
            type: String,
            default: null,
        },
        ruleColor: {
            type: String,
            default: null,
        },
        bgColor: {
            type: String,
            default: null,
        },
        topPadding: {
            type: Number,
            default: 50,
        },
        bottomPadding: {
            type: Number,
            default: 50,
        },
    },
    computed: {
        columnsClass() {
            return COLUMN_CLASSES[this.cardsPerRow] ?? COLUMN_CLASSES[2]
        },
    },
    methods: {
        spanClass(item) {
            if (!item?.fullWidth) return ''

            return SPAN_CLASSES[this.cardsPerRow] ?? SPAN_CLASSES[2]
        },
        accentStyle(item) {
            const color = item?.accentColor || this.accentColor

            return color ? `border-color: ${color}` : ''
        },
        cardStyle(item) {
            const color = item?.cardBgColor || this.cardBgColor

            return color ? `background-color: ${color}` : ''
        },
    },
}
</script>

<style scoped>
/* The card body is editor-authored HTML, so give the common tags sane rhythm
   instead of leaving them to the page's base styles. Mirrors the FAQ widget. */
.accent-card-body :deep(p) {
    margin-bottom: 0.75rem;
}

.accent-card-body :deep(p:last-child) {
    margin-bottom: 0;
}

.accent-card-body :deep(a) {
    text-decoration: underline;
}

.accent-card-body :deep(ul),
.accent-card-body :deep(ol) {
    margin: 0.5rem 0 0.75rem;
    padding-inline-start: 1.25rem;
}

.accent-card-body :deep(ul) {
    list-style: disc;
}

.accent-card-body :deep(ol) {
    list-style: decimal;
}

.accent-card-body :deep(li) {
    margin-bottom: 0.25rem;
}
</style>
